import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IFuelType } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';

const CreateFuelTypeForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['fuel-types', id],
    invalidateQueryKey: ['fuel-types'],
    id,
    singleGetApi: `/admin/fuel-types/${id}`,
    addApi: '/admin/fuel-types',
    editApi: `/admin/fuel-types/${id}`,
    listRoute: '/fuel-types',
    itemName: t('fuel_type')
  });

  const validationSchema = Yup.object({
    code: Yup.string().required(t('required'))?.max(3, t('max_3')),
    name: Yup.object().shape({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    }),
    description: Yup.object().shape({
      ar: Yup.string().required(t('required')),
      en: Yup.string().required(t('required'))
    })
  });

  const initialValues: IFuelType = {
    ...apiData,
    
    name: {
      en: apiData?.name?.en || '',
      ar: apiData?.name?.ar || ''
    },
    
    description: apiData?.description || '',
    code: apiData?.code || ''
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [
            { columns: 2, items: 1 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2, type: 'textarea' }
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('fuel_type_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper title={t('code')} inputName={`code`} inputPlaceholder={t('code')} input customPadding />
                  <FieldWrapper />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`name[en]`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`name[ar]`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('description_en')} inputName={`description[en]`} inputPlaceholder={t('description_en')} textArea customPadding />
                  <FieldWrapper title={t('description_ar')} inputName={`description[ar]`} inputPlaceholder={t('description_ar')} textArea customPadding />
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateFuelTypeForm;
